// "Your time here" — the organisation page's first tab, worked out from what
// the person told us (homes, people, dates) and, when the key is open, what
// the records say (POST /organisations/{id}/profile: who is named and when,
// records per home, school years). Pure functions, tested in
// orgProfile.test.js; components/organisation/OrgTimeHere.jsx renders them.
import { namesLikelyMatch, fullerName } from './nameMatch';
import { roleLabel, CARER_LED_TYPES } from './orgTypes';
import { UK_YEAR_GROUPS } from './schoolYears';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const parseYm = (s) => {
  const m = /^(\d{4})(?:-(\d{1,2}))?/.exec(String(s || ''));
  if (!m) return null;
  return { y: +m[1], m: m[2] ? +m[2] : null };
};
const thisMonth = (today = new Date()) => `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

export const samePerson = (a, b) => {
  if (!a || !b) return false;
  return a.trim().toLowerCase() === b.trim().toLowerCase() || namesLikelyMatch(a, b);
};

/** Whole months from one `YYYY-MM` to another (or to now when `end` is blank). */
export const monthsBetween = (start, end, today) => {
  const a = parseYm(start);
  const b = parseYm(end || thisMonth(today));
  if (!a || !b || !a.m || !b.m) return null;
  const n = (b.y - a.y) * 12 + (b.m - a.m);
  return n >= 0 ? n : null;
};

/** "10 years, 10 months" · "6 months" · "less than a month". */
export const spanWords = (start, end, today) => {
  const n = monthsBetween(start, end, today);
  if (n === null) return null;
  if (n < 1) return 'less than a month';
  const y = Math.floor(n / 12);
  const m = n % 12;
  return [y ? `${y} year${y === 1 ? '' : 's'}` : null, m ? `${m} month${m === 1 ? '' : 's'}` : null].filter(Boolean).join(', ');
};

/** "1 YR 8 MO" for the evidence line on a home's card. */
export const spanShort = (start, end, today) => {
  const n = monthsBetween(start, end, today);
  if (n === null) return null;
  if (n < 1) return 'UNDER A MONTH';
  const y = Math.floor(n / 12);
  const m = n % 12;
  return [y ? `${y} YR${y === 1 ? '' : 'S'}` : null, m ? `${m} MO` : null].filter(Boolean).join(' ');
};

/** Age in whole years at a `YYYY-MM` (or a full date), from a date of birth. */
export const ageAt = (dob, when) => {
  const d = parseYm(dob);
  const w = parseYm(when);
  if (!d || !w) return null;
  const dm = d.m || 1;
  const wm = w.m || 1;
  const age = w.y - d.y - (wm < dm ? 1 : 0);
  return age >= 0 && age < 30 ? age : null;
};

export const monthYearCaps = (ym) => {
  const p = parseYm(ym);
  if (!p) return '';
  return p.m ? `${MONTHS[p.m - 1].toUpperCase()} ${p.y}` : String(p.y);
};

/** "MAR 2008 – JAN 2019", "SEP 2012 – NOW", or null with no start. */
export const rangeCaps = (start, end, ongoingWord = 'NOW') => {
  if (!start && !end) return null;
  return `${start ? monthYearCaps(start) : '?'} – ${end ? monthYearCaps(end) : ongoingWord}`;
};

/** "2010 – 2013", "2012", or null. */
export const yearSpan = (first, last) => {
  const a = parseYm(first)?.y;
  const b = parseYm(last)?.y;
  if (!a && !b) return null;
  if (!a || !b || a === b) return String(a || b);
  return `${a} – ${b}`;
};

// Mirrors backend services/org_profile._ROLE_GROUPS, for people the person
// added themselves (their role is one of lib/orgTypes's values).
const ROLE_GROUPS = {
  social_worker: 'social_worker',
  leaving_care_pa: 'personal_adviser',
  iro: 'iro',
  foster_carer: 'carer',
  key_worker: 'home_staff', residential_worker: 'home_staff', home_manager: 'home_staff', deputy_manager: 'home_staff',
  teacher: 'school', form_tutor: 'school', head_of_year: 'school', headteacher: 'school', senco: 'school', designated_teacher: 'school',
  gp: 'health', practice_nurse: 'health', health_visitor: 'health', consultant: 'health', paediatrician: 'health',
  nurse: 'health', doctor: 'health', psychiatrist: 'health', psychologist: 'health', therapist: 'health', camhs_worker: 'health',
};
// A role typed or confirmed in the record's own words ("Deputy Headteacher
// & SENCo") is grouped by what it says.
const WRITTEN_ROLE_GROUPS = [
  [/personal advis|leaving care/i, 'personal_adviser'],
  [/reviewing officer|\biro\b/i, 'iro'],
  [/supervising|fostering social worker|link worker/i, 'other'],
  [/social worker|case ?worker/i, 'social_worker'],
  [/key ?worker|residential|home manager|deputy manager/i, 'home_staff'],
  [/teach|tutor|senco|sendco|head of year|head ?teacher|headmaster|headmistress|principal|learning mentor|pastoral|year leader|head of house|school/i, 'school'],
  [/\bgp\b|doctor|nurse|health visitor|paediatric|consultant|psychiatr|psycholog|therap|counsell|camhs/i, 'health'],
  [/foster carer|kinship/i, 'carer'],
];
export const groupForRole = (role) => {
  if (ROLE_GROUPS[role]) return ROLE_GROUPS[role];
  const hit = WRITTEN_ROLE_GROUPS.find(([rx]) => rx.test(role || ''));
  return hit ? hit[1] : 'other';
};

// Section titles, in page order. Which appear depends on who's in them.
export const PEOPLE_GROUPS = [
  { id: 'social_worker', title: 'Your social workers' },
  { id: 'personal_adviser', title: 'Your personal advisers' },
  { id: 'carer', title: 'Who looked after you' },
  { id: 'home_staff', title: 'Staff who looked after you' },
  { id: 'school', title: 'Staff who knew you' },
  { id: 'health', title: 'Doctors, nurses and therapists' },
  { id: 'iro', title: 'Your reviewing officers' },
  { id: 'other', title: 'Others who were involved' },
  { id: 'sibling', title: 'Brothers and sisters' },
];

const findIn = (profilePeople, name) => (profilePeople || []).find(
  (p) => samePerson(p.name, name) || (p.other_names || []).some((n) => samePerson(n, name)),
);

/**
 * Everyone the page lists, in groups: the people the person added (with
 * their index, so they can be removed), carers from each home, short-break
 * carers, and brothers and sisters — each with what the records add when
 * the profile is open (how many records name them, first and last month).
 * Records-only names aren't listed: they arrive as suggestions to confirm
 * or ignore, so an ignored one stays ignored.
 */
export const buildPeople = ({ org, profile, respitePool = [], siblings = [], showSiblings = false }) => {
  const people = [];
  const add = (entry) => {
    const existing = people.find((p) => p.group === entry.group && samePerson(p.name, entry.name));
    if (existing) {
      existing.name = fullerName(existing.name, entry.name);
      existing.detail = existing.detail || entry.detail;
      if (existing.index === undefined) existing.index = entry.index;
      return;
    }
    people.push(entry);
  };

  (org.social_workers || []).forEach((sw, index) => {
    if (!sw?.name) return;
    add({ name: sw.name, group: groupForRole(sw.role), role: roleLabel(org.type, sw.role), datesKnown: sw.dates_known || null, index });
  });
  for (const p of org.placements || []) {
    if (!CARER_LED_TYPES.has(p.type)) continue;
    const carers = (p.carer_names || []).filter(Boolean);
    if (!carers.length) continue;
    add({
      name: carers.join(' and '), group: 'carer',
      role: p.type === 'kinship' ? 'Family carer' : p.type === 'supported_lodgings' ? 'Supported lodgings host' : 'Foster carers',
      datesKnown: yearSpan(p.start_date, p.end_date), parts: carers,
    });
  }
  const respiteIds = new Set((org.placements || []).flatMap((p) => (p.respite || []).map((e) => e.carer_id)));
  for (const c of respitePool) {
    if (!respiteIds.has(c.id) || !c.name) continue;
    add({ name: c.name, group: 'carer', role: 'Short breaks', datesKnown: null });
  }
  if (showSiblings) {
    for (const s of siblings) {
      if (!s?.name) continue;
      add({ name: s.name, group: 'sibling', role: s.relationship && s.relationship !== 'Sibling' ? s.relationship : 'Brother or sister', siblingId: s.id });
    }
  }

  // What the records add.
  const profilePeople = profile?.people || [];
  for (const p of people) {
    const names = p.parts || [p.name];
    const hits = names.map((n) => findIn(profilePeople, n)).filter(Boolean);
    if (!hits.length) continue;
    p.records = Math.max(...hits.map((h) => h.records || 0));
    const firsts = hits.map((h) => h.first).filter(Boolean).sort();
    const lasts = hits.map((h) => h.last).filter(Boolean).sort();
    p.first = firsts[0] || null;
    p.last = lasts[lasts.length - 1] || null;
  }
  for (const p of people) p.years = p.datesKnown || yearSpan(p.first, p.last);

  // Within a group: in the order they came into your life, then most written about.
  const order = (p) => p.first || (/\d{4}/.exec(p.years || '') || ['9999'])[0];
  return PEOPLE_GROUPS.map((g) => ({
    ...g,
    people: people.filter((p) => p.group === g.id).sort((a, b) => order(a).localeCompare(order(b)) || (b.records || 0) - (a.records || 0)),
  })).filter((g) => g.people.length);
};

/** The homes in the order you lived in them. */
export const sortedHomes = (placements = []) => [...placements].sort((a, b) => (a.start_date || '9999').localeCompare(b.start_date || '9999'));

/**
 * The strip across the top of "Where you lived": one segment per home,
 * sized by how long you were there, with any gap between homes kept as a
 * blank. Only homes with a start date can be placed; null when fewer than
 * one can.
 */
export const homesStrip = (placements = [], today) => {
  const dated = sortedHomes(placements).filter((p) => parseYm(p.start_date)?.m);
  if (!dated.length) return null;
  const segments = [];
  let cursor = null;
  for (const p of dated) {
    const end = p.end_date && parseYm(p.end_date)?.m ? p.end_date : thisMonth(today);
    if (cursor) {
      const gap = monthsBetween(cursor, p.start_date, today);
      if (gap && gap > 1) segments.push({ gap: true, months: gap });
    }
    segments.push({ placement: p, months: Math.max(1, monthsBetween(p.start_date, end, today) || 1) });
    if (!cursor || end > cursor) cursor = end;
  }
  const startYear = parseYm(dated[0].start_date).y;
  const endYear = parseYm(cursor).y;
  return { segments, startYear, endYear };
};

/** Years to label under the strip: first, last, and a few evenly between. */
export const stripTicks = (startYear, endYear, max = 6) => {
  if (!startYear || !endYear) return [];
  const span = endYear - startYear;
  if (span <= 0) return [startYear];
  const step = Math.max(1, Math.ceil(span / (max - 1)));
  const ticks = [];
  for (let y = startYear; y < endYear; y += step) ticks.push(y);
  ticks.push(endYear);
  return ticks;
};

const PLACEMENT_KIND = {
  foster_placement: ['foster home', 'foster homes'],
  kinship: ['with family', 'with family'],
  supported_lodgings: ['supported lodgings', 'supported lodgings'],
  childrens_home: ["children's home", "children's homes"],
  residential: ['residential unit', 'residential units'],
  semi_independent: ['semi-independent', 'semi-independent'],
};
const WORD = ['', 'a', '2', '3', '4', '5', '6', '7', '8', '9'];

/** "family, 2 foster homes, a children's home, then semi-independent" — in the order lived. */
export const homesMix = (placements = []) => {
  const order = [];
  const counts = {};
  for (const p of sortedHomes(placements)) {
    if (!PLACEMENT_KIND[p.type]) continue;
    if (!(p.type in counts)) order.push(p.type);
    counts[p.type] = (counts[p.type] || 0) + 1;
  }
  const parts = order.map((t) => {
    const [one, many] = PLACEMENT_KIND[t];
    if (t === 'kinship') return 'family';
    if (t === 'semi_independent') return 'semi-independent';
    const n = counts[t];
    return n === 1 ? `${one === 'supported lodgings' ? '' : 'a '}${one}`.trim() : `${WORD[n] || n} ${many}`;
  });
  if (parts.length <= 1) return parts[0] || '';
  return `${parts.slice(0, -1).join(', ')}, then ${parts[parts.length - 1]}`;
};

/**
 * The "at a glance" numbers for this organisation, by what kind it is. Each
 * tile is `{ id, label, value, hint }`; a tile with nothing to count is
 * left out rather than shown as a zero.
 */
export const glanceTiles = ({ org, groups, profile, homesWhileHere = [], yearGroups = [] }) => {
  const count = (id) => groups.find((g) => g.id === id)?.people.length || 0;
  const longest = (id) => {
    const people = groups.find((g) => g.id === id)?.people || [];
    const ranked = people
      .map((p) => ({ p, n: monthsBetween(p.first, p.last) }))
      .filter((x) => x.n !== null && x.n >= 6)
      .sort((a, b) => b.n - a.n);
    if (!ranked.length || people.length < 2) return null;
    const yrs = Math.round(ranked[0].n / 12);
    return `the longest was ${ranked[0].p.name}, about ${yrs <= 1 ? 'a year' : `${yrs} years`}`;
  };
  const tiles = [];
  const homes = org.placements || [];
  const placing = ['council', 'fostering_agency'].includes(org.type);
  if (placing) {
    if (homes.length) {
      tiles.push({
        id: 'homes', label: homes.length === 1 ? 'Home' : 'Homes', value: homes.length,
        hint: [homes.length > 1 ? `${homes.length - 1} move${homes.length === 2 ? '' : 's'}` : null, homesMix(homes)].filter(Boolean).join(' · '),
      });
    }
    const sw = count('social_worker');
    if (sw) tiles.push({ id: 'social_workers', label: sw === 1 ? 'Social worker' : 'Social workers', value: sw, hint: longest('social_worker') });
    const iro = count('iro');
    const pa = count('personal_adviser');
    if (iro) tiles.push({ id: 'iros', label: iro === 1 ? 'Reviewing officer' : 'Reviewing officers', value: iro, hint: pa ? `and ${pa === 1 ? 'a personal adviser' : `${pa} personal advisers`}` : null });
    else if (pa) tiles.push({ id: 'pas', label: pa === 1 ? 'Personal adviser' : 'Personal advisers', value: pa, hint: null });
    const breaks = groups.find((g) => g.id === 'carer')?.people.filter((p) => p.role === 'Short breaks') || [];
    if (breaks.length) tiles.push({ id: 'breaks', label: 'Short breaks', value: breaks.length, hint: breaks.map((b) => b.name).slice(0, 2).join(', ') });
  } else if (org.type === 'school') {
    if (yearGroups.length) tiles.push({ id: 'years', label: yearGroups.length === 1 ? 'School year' : 'School years', value: yearGroups.length, hint: yearGroups.length > 1 ? `${yearGroups[0].label} to ${yearGroups[yearGroups.length - 1].label}` : yearGroups[0].label });
    const forms = schoolNumbers(org, profile).forms;
    if (forms.length > 1) {
      const changed = yearGroups.filter((y) => (y.forms || []).length > 1).map((y) => y.label);
      tiles.push({
        id: 'forms', label: 'Form groups', value: forms.length,
        hint: changed.length ? `you changed form in ${changed.length > 1 ? `${changed.slice(0, -1).join(', ')} and ${changed[changed.length - 1]}` : changed[0]}` : `${forms[0].value} to ${forms[forms.length - 1].value}`,
      });
    }
    // At a school, everyone named is staff, whatever their exact role.
    const staff = groups.filter((g) => g.id !== 'sibling').reduce((n, g) => n + g.people.length, 0);
    if (staff) tiles.push({ id: 'staff', label: 'Staff named', value: staff, hint: null });
  } else if (org.type === 'childrens_home') {
    const staff = count('home_staff');
    if (staff) tiles.push({ id: 'staff', label: 'Staff named', value: staff, hint: longest('home_staff') });
  } else if (['doctor', 'hospital', 'mental_health'].includes(org.type)) {
    const h = count('health');
    if (h) tiles.push({ id: 'clinicians', label: h === 1 ? 'Clinician named' : 'Clinicians named', value: h, hint: null });
  } else {
    const all = groups.filter((g) => g.id !== 'sibling').reduce((n, g) => n + g.people.length, 0);
    if (all) tiles.push({ id: 'people', label: 'People named', value: all, hint: null });
  }
  if (!placing && homesWhileHere.length) {
    const moves = homesWhileHere.length - 1;
    tiles.push({ id: 'homes_while', label: 'Homes while here', value: homesWhileHere.length, hint: moves ? `you moved ${moves === 1 ? 'once' : `${moves} times`} while you were here` : 'you stayed in one home' });
  }
  if (profile?.records) {
    tiles.push({ id: 'records', label: 'Records', value: profile.records, hint: profile.first_date ? `dated ${yearSpan(profile.first_date, profile.last_date)}` : null });
  }
  return tiles.slice(0, 4);
};

/**
 * The homes from other organisations (a council's placements) that overlap
 * this organisation's dates — "where you were living at the time" on a
 * school, GP or hospital page. Each `{ placement, org }`, clipped to the
 * overlap so the strip lines up with this organisation's time.
 */
export const homesDuring = (org, allOrgs = [], today) => {
  const start = org.start_date;
  if (!start) return [];
  const end = org.end_date || thisMonth(today);
  const out = [];
  for (const o of allOrgs) {
    if (o.id === org.id || !['council', 'fostering_agency'].includes(o.type)) continue;
    for (const p of o.placements || []) {
      const ps = p.start_date;
      const pe = p.end_date || thisMonth(today);
      if (!ps || ps > end || pe < start) continue;
      out.push({ org: o, placement: { ...p, start_date: ps < start ? start : ps, end_date: pe > end ? end : pe } });
    }
  }
  return out.sort((a, b) => a.placement.start_date.localeCompare(b.placement.start_date));
};

// ---- A school's numbers: the reference numbers they used, the form and
// registration groups (which say the school year), and class codes.

const FORM_LABEL = /\b(form|reg(istration)?|tutor|house)\b/i;
const CLASS_LABEL = /\b(class|set|teaching group)\b/i;
const LOOKS_LIKE_FORM = /^\d{1,2}\s?[A-Z]{1,4}$/i; // "8BA", "10 JCH"

/** The school year a form or class code starts with ("9BA", "10 JCH", "7Y/HI1"). */
export const formYear = (value) => {
  const m = /^\s*(\d{1,2})(?!\d)/.exec(String(value || ''));
  const n = m ? +m[1] : null;
  return n && n >= 1 && n <= 13 ? n : null;
};

/** "UPN: U81…, Exam number: 9020, 8BA" → [{ label, value }], in order. */
export const parseReferenceNumbers = (text) => String(text || '')
  .split(/[,;\n]+/)
  .map((part) => part.trim())
  .filter(Boolean)
  .map((part) => {
    const m = /^([^:]{1,40}):\s*(.+)$/.exec(part);
    return m ? { label: m[1].trim(), value: m[2].trim() } : { label: null, value: part };
  });

const tidyLabel = (label) => {
  if (!label) return 'Reference';
  if (/^[A-Z]{2,5}$/.test(label)) return label; // UPN, ULN, UCI
  const lower = label.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

/**
 * A school's numbers sorted out: `ids` (labelled, one per value), `forms`
 * (form/registration groups, with their year) and `classes` (class codes,
 * with their year) — from the organisation's reference numbers and the
 * form groups the records name.
 */
export const schoolNumbers = (org, profile) => {
  const ids = [];
  const forms = [];
  const classes = [];
  const add = (list, entry) => {
    if (!list.some((x) => x.value.replace(/\s/g, '').toUpperCase() === entry.value.replace(/\s/g, '').toUpperCase())) list.push(entry);
  };
  for (const { label, value } of parseReferenceNumbers(org.reference_numbers)) {
    if (label && CLASS_LABEL.test(label)) add(classes, { value: value.toUpperCase(), year: formYear(value) });
    else if ((label && FORM_LABEL.test(label)) || (!label && org.type === 'school' && LOOKS_LIKE_FORM.test(value))) add(forms, { value: value.toUpperCase(), year: formYear(value) });
    else add(ids, { label: tidyLabel(label), value });
  }
  for (const f of profile?.form_groups || []) add(forms, { value: f.value, year: f.year ?? formYear(f.value), records: f.records });
  const byYear = (a, b) => (a.year ?? 99) - (b.year ?? 99) || a.value.localeCompare(b.value);
  return { ids, forms: forms.sort(byYear), classes: classes.sort(byYear) };
};

/**
 * The school years someone was at a school, as `{ label, number, forms,
 * classes, records }`: from the years they gave (year_group_start/end,
 * lib/schoolYears values), else the span the form groups and records
 * name. Empty when nothing says.
 */
export const schoolYearsFor = (org, profile, groups = UK_YEAR_GROUPS) => {
  const idx = (v) => groups.find((g) => g.value === v)?.index;
  const { forms, classes } = schoolNumbers(org, profile);
  let from = idx(org.year_group_start);
  let to = idx(org.year_group_end);
  if (from === undefined && to === undefined) {
    const seen = [
      ...(profile?.year_groups || []).map((y) => y.number),
      ...forms.map((f) => f.year),
    ].filter((n) => typeof n === 'number' && n >= 0);
    if (!seen.length) return [];
    from = Math.min(...seen);
    to = Math.max(...seen);
  }
  if (from === undefined) from = to;
  if (to === undefined) to = from;
  return groups
    .filter((g) => g.index >= Math.min(from, to) && g.index <= Math.max(from, to))
    .map((g) => ({
      label: g.label,
      number: g.index,
      forms: forms.filter((f) => f.year === g.index).map((f) => f.value),
      classes: classes.filter((c) => c.year === g.index).length,
      records: (profile?.year_groups || []).find((y) => y.number === g.index)?.records || 0,
    }));
};
